/**
 * The track record: hand-maintained section boundaries per track (web/public/tracks.json,
 * published with the site). It is small, so it is fetched fresh on every load; the app falls
 * back to the copy in its bundle when the site cannot be reached.
 */

export interface SectionDef {
  name: string
  /** Where the section ends: a point on the track, placed on the reference lap at run time */
  at?: { lat: number; lon: number }
  /** Metres from the start/finish where the section ends on the lap the mark was taken from; the fallback */
  to?: number
}

export interface TrackDef {
  sections: SectionDef[]
}

export type TrackRecord = Record<string, TrackDef>

const SITE = 'https://mala-racing.com'

export async function loadTrackRecord(): Promise<TrackRecord> {
  const local = '/tracks.json'
  const web = typeof location !== 'undefined' && /^https?:$/.test(location.protocol)
  // On the website the file is same-origin; the app asks the site first (always current) and
  // keeps its bundled copy for when the phone is offline.
  const urls = web ? [local] : [`${SITE}${local}`, local]
  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: 'no-store' })
      if (!res.ok) continue
      return normalize((await res.json()) as Record<string, unknown>)
    } catch {
      /* try the next source */
    }
  }
  return {}
}

/** Keep only well-formed entries; keys are matched to the logger's track name case-insensitively. */
export function normalize(raw: Record<string, unknown>): TrackRecord {
  const out: TrackRecord = {}
  for (const [name, def] of Object.entries(raw)) {
    if (name.startsWith('_') || !def || typeof def !== 'object') continue
    const sections = (def as { sections?: unknown }).sections
    if (!Array.isArray(sections)) continue
    const clean: SectionDef[] = []
    for (const s of sections as Array<{ name?: unknown; to?: unknown; at?: unknown }>) {
      if (!s || typeof s !== 'object') continue
      const to = typeof s.to === 'number' && Number.isFinite(s.to) && s.to > 0 ? s.to : undefined
      const a = s.at as { lat?: unknown; lon?: unknown } | undefined
      const at =
        a &&
        typeof a === 'object' &&
        typeof a.lat === 'number' &&
        typeof a.lon === 'number' &&
        Number.isFinite(a.lat) &&
        Number.isFinite(a.lon)
          ? { lat: a.lat, lon: a.lon }
          : undefined
      clean.push({ name: typeof s.name === 'string' ? s.name : `Section ${clean.length + 1}`, to, at })
    }
    if (clean.length) out[name] = { sections: clean }
  }
  return out
}

export function findTrack(record: TrackRecord, trackName: string | undefined): TrackDef | undefined {
  if (!trackName) return undefined
  const want = trackName.trim().toLowerCase()
  for (const [name, def] of Object.entries(record)) if (name.trim().toLowerCase() === want) return def
  return undefined
}
